import { directSupabase } from '../lib/supabaseClient';
import { emitActivityEventBackground } from './activityEvents.service';
import { jobActualsService } from './jobActualsService';
import type { JobActuals } from '../types/jobActuals.types';
import {
  assembleReviewQueue,
  buildLabourLines,
  ACCEPTED_QUOTE_STATUSES,
  canMarkReviewed,
  installedOn,
  isActualsReminderDueOn,
  isInvoiceLabourSource,
  isJobRecordComplete,
  JOB_RECORD_BLOCKS_COMPLETE_MESSAGE,
  selectReviewBasis,
  type JobReviewRecord,
  type JobReviewStatus,
  type ReviewBasis,
  type ReviewEvent,
  type ReviewExtraCost,
  type ReviewJobCard,
  type ReviewLabourLine,
  type ReviewProject,
  type ReviewQueueRow,
  type ReviewQuote,
  type ReviewQuoteLine,
  type RateComponents,
  type ReviewSubcontractorCost,
  type ReviewWorkPerson,
  componentsOn,
  quoteDateOf,
} from '../pages/job-records/jobReview.model';
import { workspaceLifecycleStatus } from '../utils/workflowUtils';
import {
  listProjectJobAssignees,
  listProjectJobCards,
  listJobSubcontractorNames,
  listProjectSubcontractors,
  listSubcontractorAccounts,
  loadLatestInstallEvent,
} from '../pages/project-workspace/jobCard.service';
import { buildCrewSchedule, cardInstallSpans, type CrewSchedule } from '../pages/project-workspace/jobCardCoverage';
import { groupCompletionsByCard, type JobCompletionRow } from '../pages/project-workspace/openJobCards.model';
import { loadJobCardCompletions } from '../pages/project-workspace/openJobCards.service';

const PROJECT_COLUMNS = 'id, project_name, customer_name, supply_only, complete, project_status, status, install_date, workflow_tasks';
const TOTALS_COLUMNS = 'admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct, gross_total';
const QUOTE_COLUMNS = `id, project_id, status, lifecycle_ref, accepted_at, created_at, active_version_id, ${TOTALS_COLUMNS}`;
const INVOICE_COLUMNS = `id, project_id, status, lifecycle_ref, invoice_ref, accepted_at, created_at, invoice_role, ${TOTALS_COLUMNS}`;
const LINE_COLUMNS = 'id, document_id, name, qty, unit, unit_price, line_total, sort_order, line_kind, vat_inclusive';
const VERSION_LINE_COLUMNS = 'id, version_id, name, qty, unit, unit_price, line_total, sort_order, line_kind, vat_inclusive';
const VERSION_HEADER_COLUMNS = 'id, admin_pct, admin_cap, contingency_pct, profit_pct, vat_enabled, vat_pct';

export const JOB_REVIEWS_MISSING = 'The job_reviews table is not in the database yet. Apply migration 20260928090000-job-reviews.sql.';

function isMissingTable(message?: string | null) {
  return /job_reviews/i.test(String(message || '')) && /does not exist|schema cache|not find/i.test(String(message || ''));
}

async function inChunks<T>(ids: string[], load: (chunk: string[]) => Promise<T[]>): Promise<T[]> {
  const unique = [...new Set(ids.filter(Boolean))];
  const rows: T[] = [];
  for (let index = 0; index < unique.length; index += 200) {
    rows.push(...await load(unique.slice(index, index + 200)));
  }
  return rows;
}

function fromReviewRow(row: any): JobReviewRecord {
  return {
    id: row.id,
    projectId: row.project_id,
    quoteDocumentId: row.quote_document_id || null,
    status: row.status === 'reviewed' ? 'reviewed' : 'to_review',
    labourLines: Array.isArray(row.labour_lines) ? row.labour_lines : [],
    extraCosts: Array.isArray(row.extra_costs) ? row.extra_costs : [],
    subcontractorCosts: Array.isArray(row.subcontractor_costs) ? row.subcontractor_costs : [],
    notes: row.notes || null,
    reviewedAt: row.reviewed_at || null,
    reviewedBy: row.reviewed_by || null,
    updatedAt: row.updated_at || null,
  };
}

async function loadReviews(projectId?: string): Promise<{ reviews: JobReviewRecord[]; tableMissing: boolean }> {
  let query = (directSupabase as any).from('job_reviews').select('*');
  if (projectId) query = query.eq('project_id', projectId);
  const { data, error } = await query;
  if (error) {
    if (isMissingTable(error.message)) return { reviews: [], tableMissing: true };
    throw new Error(error.message);
  }
  return { reviews: (data || []).map(fromReviewRow), tableMissing: false };
}

/** Every dated split of the Company Flat Rate. Empty when the table isn't in the database yet. */
export async function loadRateComponents(): Promise<RateComponents[]> {
  const columns = 'effective_from, directors_per_day, overhead_per_day, fitting_labour_per_day';
  const select = (extra: string) => (directSupabase as any).from('company_rate_components').select(`${columns}${extra}`);
  let result = await select(', fitting_labour_email');
  if (result.error) result = await select('');
  if (result.error) return [];
  return ((result.data || []) as Array<Record<string, unknown>>).map((row) => ({
    effectiveFrom: String(row.effective_from || '').slice(0, 10),
    directorsPerDay: Number(row.directors_per_day) || 0,
    overheadPerDay: Number(row.overhead_per_day) || 0,
    fittingPerDay: Number(row.fitting_labour_per_day) || 0,
    fittingEmail: row.fitting_labour_email ? String(row.fitting_labour_email).toLowerCase() : null,
  }));
}

async function applyVersionTotals(docs: ReviewQuote[]): Promise<ReviewQuote[]> {
  const versionIds = docs.map((doc) => doc.active_version_id).filter((id): id is string => !!id);
  if (!versionIds.length) return docs;
  const versions = await inChunks<ReviewQuote & { id: string }>(versionIds, async (chunk) => {
    const { data, error } = await directSupabase
      .from('document_versions')
      .select(VERSION_HEADER_COLUMNS)
      .in('id', chunk);
    if (error) throw new Error(error.message);
    return (data || []) as unknown as Array<ReviewQuote & { id: string }>;
  });
  const byId = new Map(versions.map((row) => [row.id, row]));
  return docs.map((doc) => {
    const version = doc.active_version_id ? byId.get(doc.active_version_id) : null;
    if (!version) return doc;
    return {
      ...doc,
      admin_pct: version.admin_pct,
      admin_cap: version.admin_cap,
      contingency_pct: version.contingency_pct,
      profit_pct: version.profit_pct,
      vat_enabled: version.vat_enabled,
      vat_pct: version.vat_pct,
      gross_total: doc.gross_total,
    };
  });
}

async function loadAcceptedQuotes(projectIds: string[]): Promise<ReviewQuote[]> {
  const docs = await inChunks<ReviewQuote>(projectIds, async (chunk) => {
    const { data, error } = await directSupabase
      .from('documents')
      .select(QUOTE_COLUMNS)
      .eq('kind', 'Quotation')
      .in('status', ACCEPTED_QUOTE_STATUSES)
      .in('project_id', chunk);
    if (error) throw new Error(error.message);
    return (data || []) as unknown as ReviewQuote[];
  });
  return applyVersionTotals(docs);
}

async function loadProjectInvoices(projectIds: string[]): Promise<ReviewQuote[]> {
  const docs = await inChunks<ReviewQuote>(projectIds, async (chunk) => {
    const { data, error } = await directSupabase
      .from('documents')
      .select(INVOICE_COLUMNS)
      .eq('kind', 'Invoice')
      .in('project_id', chunk);
    if (error) throw new Error(error.message);
    return (data || []) as unknown as ReviewQuote[];
  });
  return applyVersionTotals(docs);
}

/** Every sold line on the quote or works invoice. Actuals are stored on the Job Record only. */
async function loadSourceLines(docs: ReviewQuote[]): Promise<ReviewQuoteLine[]> {
  const documentByVersion = new Map<string, string>();
  docs.forEach((doc) => {
    if (doc.active_version_id) documentByVersion.set(doc.active_version_id, doc.id);
  });
  const baseIds = docs.filter((doc) => !doc.active_version_id).map((doc) => doc.id);

  const versionLines = await inChunks<ReviewQuoteLine>([...documentByVersion.keys()], async (chunk) => {
    const { data, error } = await directSupabase
      .from('document_version_lines')
      .select(VERSION_LINE_COLUMNS)
      .in('version_id', chunk);
    if (error) throw new Error(error.message);
    return (data || []).map(({ version_id, ...line }: any) => ({
      ...line,
      document_id: documentByVersion.get(version_id) as string,
    }));
  });
  const baseLines = await inChunks<ReviewQuoteLine>(baseIds, async (chunk) => {
    const { data, error } = await directSupabase
      .from('document_lines')
      .select(LINE_COLUMNS)
      .in('document_id', chunk);
    if (error) throw new Error(error.message);
    return (data || []) as unknown as ReviewQuoteLine[];
  });
  return [...versionLines, ...baseLines];
}

async function loadEvents(projectIds: string[]): Promise<ReviewEvent[]> {
  return inChunks<ReviewEvent>(projectIds, async (chunk) => {
    const { data, error } = await directSupabase
      .from('project_events')
      .select('project_id, event_type, event_date, end_date, status')
      .in('project_id', chunk)
      .in('event_type', ['installationDateConfirmation', 'deliveryDateConfirmation']);
    if (error) throw new Error(error.message);
    return (data || []) as unknown as ReviewEvent[];
  });
}

export type ReviewJobCards = { cards: ReviewJobCard[]; completions: JobCompletionRow[] };

/** Job cards and their completions: the cards still carrying a job decide its install date. */
async function loadReviewJobCards(projectIds: string[]): Promise<ReviewJobCards> {
  const cards = await inChunks<ReviewJobCard>(projectIds, async (chunk) => {
    const { data, error } = await (directSupabase as any)
      .from('project_job_cards')
      .select('id, project_id, install_date_override, install_end_date_override, follows_booking')
      .in('project_id', chunk);
    // Without the date columns, fall back to the calendar booking
    if (error) return [];
    return (data || []) as ReviewJobCard[];
  });
  const completions = await inChunks<JobCompletionRow>(
    Array.from(new Set(cards.map((card) => card.project_id))),
    loadJobCardCompletions,
  );
  return { cards, completions };
}

async function loadNames(profileIds: string[]): Promise<Map<string, string>> {
  const names = new Map<string, string>();
  const ids = [...new Set(profileIds.filter(Boolean))];
  if (!ids.length) return names;
  const { data } = await directSupabase.from('profiles').select('id, full_name, email').in('id', ids);
  (data || []).forEach((row: { id: string; full_name?: string | null; email?: string | null }) => {
    const name = row.full_name || row.email;
    if (name) names.set(row.id, name);
  });
  return names;
}

function londonToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
}

export type ReviewQueue = {
  inProgress: ReviewQueueRow[];
  toReview: ReviewQueueRow[];
  reviewed: ReviewQueueRow[];
  tableMissing: boolean;
};

async function buildQueue(
  projects: ReviewProject[],
  withDetail: boolean,
  knownEvents?: ReviewEvent[],
  knownJobCards?: ReviewJobCards,
): Promise<ReviewQueue> {
  const { reviews, tableMissing } = await loadReviews();
  const { data: legacyRows, error: legacyError } = await directSupabase.from('job_actuals').select('project_id');
  if (legacyError) throw new Error(legacyError.message);
  const legacyProjectIds = new Set((legacyRows || []).map((row: { project_id: string }) => row.project_id));
  const candidates = projects.filter((project) => workspaceLifecycleStatus(project) !== 'Cancelled');
  const candidateIds = candidates.map((project) => project.id);

  const quotes = await loadAcceptedQuotes(candidateIds);
  const invoices = await loadProjectInvoices(candidateIds);
  const labourInvoices = invoices.filter((invoice) => isInvoiceLabourSource(invoice));
  const [quoteLines, invoiceLines] = await Promise.all([
    loadSourceLines(quotes),
    loadSourceLines(labourInvoices),
  ]);
  // Bookings decide when a Job Record is due, so they are always needed
  const events = knownEvents || await loadEvents(candidateIds);
  const jobCards = knownJobCards || await loadReviewJobCards(candidateIds);
  const reviewerNames = withDetail ? await loadNames(reviews.map((review) => review.reviewedBy || '')) : new Map<string, string>();
  const rateComponents = await loadRateComponents();

  const queue = assembleReviewQueue({
    projects: candidates,
    quotes,
    quoteLines,
    invoices,
    invoiceLines,
    reviews,
    legacyProjectIds,
    events,
    jobCards: jobCards.cards,
    completions: jobCards.completions,
    reviewerNames,
    rateComponents,
    today: londonToday(),
  });
  return { ...queue, tableMissing };
}

export async function loadReviewQueue(): Promise<ReviewQueue> {
  const { data: projectRows, error } = await directSupabase.from('projects').select(PROJECT_COLUMNS).limit(5000);
  if (error) throw new Error(error.message);
  return buildQueue((projectRows || []) as unknown as ReviewProject[], true);
}

/** Open workspaces with an install or delivery date booked and a Job Record that is not complete yet. */
/** Workspaces whose install (or delivery) has happened but whose Job Record is not done. */
export async function loadActualsDueProjectIds(
  projects: ReviewProject[],
  events?: ReviewEvent[],
  jobCards?: ReviewJobCards,
): Promise<Set<string>> {
  const queue = await buildQueue(projects, false, events, jobCards);
  const today = londonToday();
  return new Set(queue.inProgress.filter((row) => isActualsReminderDueOn(row, today)).map((row) => row.projectId));
}

export type JobReviewDetail = {
  project: ReviewProject;
  quote: ReviewQuote | null;
  sources: ReviewQuote[];
  basis: ReviewBasis;
  labourLines: ReviewLabourLine[];
  review: JobReviewRecord | null;
  legacy: JobActuals[];
  installedOn: string | null;
  reviewerName: string | null;
  tableMissing: boolean;
  /** Who was booked on site on which days, from the workspace's job cards. */
  crewSchedule: CrewSchedule | null;
  /** The flat rate split in force on the quote's date; null when no split is set up. */
  rateComponents: RateComponents | null;
  /** Subcontractors on this workspace's job cards, offered when adding subcontractor costs. */
  jobCardSubcontractors: Array<{ accountId: string; name: string }>;
  /** Every subcontractor in the directory, to pick from. */
  subcontractorAccounts: Array<{ accountId: string; name: string }>;
  /** Everyone on the job cards with their days on site; `covered` = the flat rate pays their day. */
  people: ReviewWorkPerson[];
  /** Name of the person whose day the flat rate's fitting part pays for (e.g. Tom). */
  pricedFitterName: string | null;
};

async function profileNameByEmail(email: string | null | undefined): Promise<string | null> {
  if (!email) return null;
  const { data } = await directSupabase
    .from('profiles')
    .select('full_name, email')
    .ilike('email', email)
    .limit(1);
  const row = (data || [])[0] as { full_name?: string | null; email?: string | null } | undefined;
  return row ? (String(row.full_name || '').trim() || null) : null;
}

type CardPerson = { key: string; name: string; email: string | null; accountId: string | null; profileId: string | null; days: Set<string> };

async function loadCrewSchedule(
  project: ReviewProject & { metadata?: unknown },
): Promise<{ schedule: CrewSchedule; people: CardPerson[] } | null> {
  const [cards, assignees, subcontractors, calendar] = await Promise.all([
    listProjectJobCards(project.id),
    listProjectJobAssignees(project.id),
    listJobSubcontractorNames({ projectId: project.id }),
    loadLatestInstallEvent(project.id),
  ]);
  if (!cards.length) return null;
  const spans = cardInstallSpans(calendar, cards, project);
  const daysByCard = new Map(spans.map((entry) => [entry.cardId, entry.span.days]));
  const people = new Map<string, CardPerson>();
  const add = (cardId: string, person: Omit<CardPerson, 'days'>) => {
    const current = people.get(person.key) || { ...person, days: new Set<string>() };
    (daysByCard.get(cardId) || []).forEach((day) => current.days.add(day));
    people.set(person.key, current);
  };
  assignees.forEach((row) => add(row.job_card_id, {
    key: `profile:${row.profile_id}`,
    name: row.full_name || row.email || 'Crew',
    email: row.email ? row.email.toLowerCase() : null,
    accountId: null,
    profileId: row.profile_id,
  }));
  subcontractors.forEach((row) => add(row.job_card_id, {
    key: `account:${row.account_id}`,
    name: row.name,
    email: null,
    accountId: row.account_id,
    profileId: null,
  }));
  const crew = [...assignees, ...subcontractors.map((row) => ({ job_card_id: row.job_card_id, full_name: row.name }))];
  return { schedule: buildCrewSchedule(spans, crew), people: Array.from(people.values()) };
}

export async function loadJobReview(projectId: string): Promise<JobReviewDetail> {
  const { data: projectRow, error } = await directSupabase
    .from('projects')
    .select(`${PROJECT_COLUMNS}, metadata`)
    .eq('id', projectId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!projectRow) throw new Error('Workspace not found.');
  const project = projectRow as unknown as ReviewProject & { metadata?: unknown };

  const [{ reviews, tableMissing }, quotes, invoices, events, jobCards, legacy, crewSchedule, rateRows, jobCardSubcontractors, accounts] = await Promise.all([
    loadReviews(projectId),
    loadAcceptedQuotes([projectId]),
    loadProjectInvoices([projectId]),
    loadEvents([projectId]),
    loadReviewJobCards([projectId]),
    jobActualsService.getByProject(projectId),
    loadCrewSchedule(project).catch(() => null),
    loadRateComponents(),
    listProjectSubcontractors(projectId).catch(() => []),
    listSubcontractorAccounts().catch(() => []),
  ]);
  const review = reviews[0] || null;
  const selected = selectReviewBasis({ projectId, quotes, invoices });
  const rateComponents = componentsOn(quoteDateOf(selected.source), rateRows);
  const sourceLines = selected.sources.length ? await loadSourceLines(selected.sources) : [];
  const [names, pricedFitterName] = await Promise.all([
    loadNames([review?.reviewedBy || '']),
    profileNameByEmail(rateComponents?.fittingEmail).catch(() => null),
  ]);

  return {
    project,
    quote: selected.source,
    sources: selected.sources,
    basis: selected.source ? selected.basis : 'quote',
    labourLines: buildLabourLines(sourceLines, review?.labourLines || []),
    review,
    legacy,
    installedOn: installedOn(project, events, {
      cards: jobCards.cards,
      completionsByCard: groupCompletionsByCard(jobCards.completions),
    }),
    reviewerName: review?.reviewedBy ? names.get(review.reviewedBy) || null : null,
    tableMissing,
    crewSchedule: crewSchedule?.schedule || null,
    rateComponents,
    jobCardSubcontractors,
    subcontractorAccounts: accounts.map((account) => ({ accountId: account.id, name: account.name })),
    people: (crewSchedule?.people || []).map((person) => ({
      id: person.key,
      name: person.name,
      accountId: person.accountId,
      profileId: person.profileId,
      days: person.days.size,
      covered: !!rateComponents?.fittingEmail && person.email === rateComponents.fittingEmail,
    })),
    pricedFitterName,
  };
}

export async function saveJobReview(input: {
  projectId: string;
  quoteDocumentId: string | null;
  labourLines: ReviewLabourLine[];
  extraCosts: ReviewExtraCost[];
  subcontractorCosts?: ReviewSubcontractorCost[];
  notes: string | null;
  status: JobReviewStatus;
  jobRef?: string | null;
}): Promise<JobReviewRecord> {
  const { data: userRes } = await directSupabase.auth.getUser();
  const userId = userRes.user?.id ?? null;
  const { reviews, tableMissing } = await loadReviews(input.projectId);
  if (tableMissing) throw new Error(JOB_REVIEWS_MISSING);
  const existing = reviews[0] || null;
  const reviewed = input.status === 'reviewed';
  if (reviewed && existing?.status !== 'reviewed') {
    const { data: project, error: projectError } = await directSupabase
      .from('projects')
      .select(PROJECT_COLUMNS)
      .eq('id', input.projectId)
      .maybeSingle();
    if (projectError) throw new Error(projectError.message);
    if (!project || !canMarkReviewed(project as unknown as ReviewProject)) {
      throw new Error('This Job Record cannot be marked reviewed on a cancelled workspace.');
    }
  }

  const payload = {
    project_id: input.projectId,
    quote_document_id: input.quoteDocumentId,
    status: input.status,
    labour_lines: input.labourLines,
    extra_costs: input.extraCosts.filter((extra) => extra.description.trim() || Number(extra.amount)),
    subcontractor_costs: (input.subcontractorCosts || []).filter((sub) => sub.name.trim() || Number(sub.amount)),
    notes: input.notes?.trim() || null,
    reviewed_at: reviewed ? (existing?.status === 'reviewed' && existing.reviewedAt) || new Date().toISOString() : null,
    reviewed_by: reviewed ? (existing?.status === 'reviewed' && existing.reviewedBy) || userId : null,
    updated_by: userId,
  };
  const query = existing
    ? (directSupabase as any).from('job_reviews').update(payload).eq('id', existing.id)
    : (directSupabase as any).from('job_reviews').insert({ ...payload, created_by: userId });
  const { data, error } = await query.select('*').single();
  if (error) throw new Error(error.message);

  const changed = (existing?.status || 'to_review') !== input.status;
  if (changed || (!existing && reviewed)) {
    const ref = input.jobRef ? ` (${input.jobRef})` : '';
    emitActivityEventBackground({
      action: reviewed ? 'job_review.reviewed' : 'job_review.reopened',
      entityType: 'project',
      entityId: input.projectId,
      projectId: input.projectId,
      actorId: userId,
      label: reviewed ? `Job actuals reviewed${ref}` : `Job actuals review reopened${ref}`,
    });
  }
  return fromReviewRow(data);
}

export async function assertJobRecordAllowsComplete(projectId: string): Promise<void> {
  const detail = await loadJobReview(projectId);
  if (isJobRecordComplete(detail.review, (detail.legacy?.length || 0) > 0)) return;
  throw new Error(JOB_RECORD_BLOCKS_COMPLETE_MESSAGE);
}

